// src/utils/validationUtils.js

/**
 * Validates if the value is a non-empty string.
 * @param {string} value - The value to validate.
 * @param {string} fieldName - The user-friendly name of the field.
 * @throws {Error} if validation fails.
 */
export const validateRequired = (value, fieldName) => {
  if (!value || typeof value !== "string" || !value.trim()) {
    throw new Error(`${fieldName} is required.`);
  }
};
/**
 * Validates a username.
 * @param {string} userName - The username to validate.
 * @param {string} fieldName - The user-friendly name of the field (default: "Username").
 * @throws {Error} if validation fails.
 */
export const validateUsername = (userName, fieldName = "Username") => {
  validateRequired(userName, fieldName);
  if (userName.length < 6) {
    throw new Error(`${fieldName} must be at least 6 characters long.`);
  }
  const usernameRegex = /^[a-zA-Z][a-zA-Z0-9_-]{5,19}$/;
  if (!usernameRegex.test(userName)) {
    throw new Error(
      `Please enter a valid ${fieldName.toLowerCase()}. Example: user123`
    );
  }
};
/**
 * Validates an email address.
 * @param {string} email - The email to validate.
 * @param {string} fieldName - The user-friendly name of the field (default: "Email").
 * @throws {Error} if validation fails.
 */
export const validateEmail = (email, fieldName = "Email") => {
  validateRequired(email, fieldName);
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    throw new Error(
      `Please enter a valid ${fieldName.toLowerCase()}. Example: user@example.com`
    );
  }
};

/**
 * Validates a password against .NET Identity like standards.
 * @param {string} password - The password to validate.
 * @param {string} fieldName - The user-friendly name of the field (default: "Password").
 * @throws {Error} if validation fails, with a detailed message.
 */
export const validateStrongPassword = (password, fieldName = "Password") => {
  validateRequired(password, fieldName);
  const minLength = 8;
  const messages = [];

  if (password.length < minLength) {
    messages.push(`be at least ${minLength} characters long`);
  }
  if (!/[a-z]/.test(password)) {
    messages.push("contain at least one lowercase letter (a-z)");
  }
  if (!/[A-Z]/.test(password)) {
    messages.push("contain at least one uppercase letter (A-Z)");
  }
  if (!/[0-9]/.test(password)) {
    messages.push("contain at least one digit (0-9)");
  }
  if (!/[^a-zA-Z0-9]/.test(password)) {
    messages.push("contain at least one special character (e.g., !@#$%^&*)");
  }

  if (messages.length > 0) {
    throw new Error(`${fieldName} must ${messages.join(", and ")}.`);
  }
};

/**
 * Validates if two passwords match.
 * @param {string} password - The primary password.
 * @param {string} confirmPassword - The confirmation password.
 * @throws {Error} if validation fails.
 */
export const validatePasswordConfirmation = (password, confirmPassword) => {
  validateRequired(confirmPassword, "Confirm Password");
  if (password !== confirmPassword) {
    throw new Error("Passwords do not match. Please re-enter them.");
  }
};
